import classNames from "classnames";
import PropTypes from 'prop-types';
// Aliased: the component defines its own local `Icon` (legacy class-string renderer) below
import CoreIcon from '@components/core/Icon';

const buttonTypeClass = (type) => {
    switch (type) {
        case 'grey-light':
            return 'btn-secondary';
        case 'primary-outline-rounded':
            return `btn-ghost`;
        case 'secondary-outline':
            return `btn-ghost-secondary`;
        case 'primary':
            return 'btn-primary';
        case 'default':
            return 'btn-default';
        case 'primary-bright':
            return 'primary-bright';
        case 'secondary':
            return 'btn-secondary';
        case 'link':
            return 'btn-link';
        case 'button-link':
            return 'button-link';
        case 'link-secondary':
            return 'btn-link btn-secondary';
        case 'link-secondary-hover':
            return 'btn-link btn-secondary with-hover';
        case 'dashed-border':
            return 'btn-dashed-border';
        // Design-system additions
        case 'ghost':
            return 'btn-ghost';
        case 'tertiary':
            return 'btn-tertiary';
        case 'text':
            return 'btn-text';
        case 'destructive': // kept for back-compat — same style as destructive-ghost
        case 'destructive-ghost':
            return 'btn-destructive';
        case 'destructive-fill':
            return 'btn-destructive-fill';
        case 'destructive-outline':
            return 'btn-destructive-outline';
        case 'destructive-text':
            return 'btn-destructive-text';
        // `.btn-ai` is the stable class every AI button carries; `hasDropdown` picks
        // the "Generate with AI" look from it in CSS (see ai-button.less).
        case 'ai':
            return 'btn-ai';
        default:
            return 'btn-primary';
    }
};
const Button = ({
    onClick,
    icon,
    title,
    disabled,
    children,
    className,
    type,
    href,
    hidden,
    isLink,
    wrapText,
    textClassName,
    size,
    onlyIcon,
    v2,
    noPadding,
    // Design-system additions
    iconLeft = null,
    iconRight = null,
    active = false,
    loading = false,
    fullWidth = false,
    // Native button type — `type` is taken by the visual variant. Defaults to
    // "button" so a Button inside a <form> never becomes its implicit submit.
    htmlType = 'button',
    hasDropdown = false,
    altTitle,
    ...rest
}) => {
    if (hidden) return null;

    const isInert = disabled || loading;

    const buttonSizes = {
        'xxs': 'btn-xxs',
        'xs': 'btn-xs',
        'sm': 'btn-sm',
        'md': 'btn-md',
        'lg': 'btn-lg',
    };
    const classes = classNames('btn',
        `${buttonTypeClass(type)}`,
        buttonSizes[size] || '',
        {
            'btn--new': v2,
            'btn-icon': icon || iconLeft || iconRight || type === 'ai',
            'disabled': disabled,
            'btn-only-icon': onlyIcon,
            'no-padding': noPadding,
            // Design-system states
            'btn-active': active,
            'btn-is-loading': loading,
            'btn-full-width': fullWidth,
        },
        className);

    const iconNode = icon ? <span className={icon}></span> : null;
    const textNode = !textClassName && !wrapText ? title : <span className={classNames('text', textClassName)}>{title}</span>;

    // Design-system extras: loading spinner, node icons and the auto AI sparkles
    const leadingNodes = (
        <>
            {loading && <span className='btn-loading-spinner' aria-hidden='true' />}
            {/* AI sparkles from the app icon set (icons/svg/ai-sparkles.svg), design AI gradient */}
            {!loading && type === 'ai' && !icon && !iconLeft && (
                <span className='btn-icon-node'>
                    <CoreIcon value='ai-sparkles' noMargin />
                </span>
            )}
            {!loading && iconLeft && <span className='btn-icon-node'>{iconLeft}</span>}
        </>
    );

    const renderTrailingNodes = () => {
        if (loading) return null;
        if (iconRight) return <span className='btn-icon-node'>{iconRight}</span>;
        if (type === 'ai' && hasDropdown) {
            return (
                <span className='btn-icon-node'>
                    <CoreIcon value='chevron-down' noMargin />
                </span>
            );
        }
        return null;
    };
    const trailingNodes = renderTrailingNodes();

    const onClickHandler = (e) => {
        // An enabled htmlType="submit" button must keep its native default
        // (submitting the form); everything else behaves as before.
        if (!href && !(htmlType === 'submit' && !isInert)) e.preventDefault();
        if (!isInert && typeof onClick === 'function') {
            onClick(e);
        }
    };

    if (href) {
        return (
            <a
                href={href}
                onClick={onClickHandler}
                className={classes}
                title={altTitle}
                aria-disabled={isInert || undefined}
                {...rest}
            >
                {leadingNodes}
                {iconNode}
                {textNode}
                {loading && onlyIcon ? null : children}
                {trailingNodes}
            </a>
        );
    }

    if (isLink) {
        return (
            <a onClick={onClickHandler} className={classes} title={altTitle} aria-disabled={isInert || undefined} {...rest}>
                {leadingNodes}
                {iconNode}
                {textNode}
                {loading && onlyIcon ? null : children}
                {trailingNodes}
            </a>
        );
    }
    return (
        <button
            type={htmlType}
            disabled={isInert}
            onClick={onClickHandler}
            className={classes}
            title={altTitle}
            {...rest}
        >
            {leadingNodes}
            {iconNode}
            {textNode}
            {loading && onlyIcon ? null : children}
            {trailingNodes}
        </button>
    );
};

// Joined button group (design-system "split"/grouped buttons).
// Own class on purpose — Bootstrap's .btn-group (used by existing dropdowns) stays untouched.
export const ButtonGroup = ({ className, children, ...rest }) => (
    <div className={classNames('btn-group-joined', className)} role='group' {...rest}>
        {children}
    </div>
);

Button.Group = ButtonGroup;

Button.propTypes = {
    /** Click handler (guarded automatically while disabled or loading) */
    onClick: PropTypes.func,
    /** Legacy icon class string, e.g. 'icon-svg-save-01' — rendered as a span */
    icon: PropTypes.string,
    /** Button label text */
    title: PropTypes.string,
    /** Disabled state — sets the native `disabled` attribute (aria-disabled on the anchor forms) */
    disabled: PropTypes.bool,
    /** Extra content after the label (or the icon when onlyIcon without iconLeft) */
    children: PropTypes.node,
    /** Extra CSS class */
    className: PropTypes.string,
    /** Visual variant — brand-red primary is reserved for the single main action; ai renders the gradient sparkles icon automatically */
    type: PropTypes.oneOf([
        'primary',
        'secondary',
        'default',
        'ghost',
        'tertiary',
        'text',
        'destructive',
        'destructive-fill',
        'destructive-outline',
        'destructive-ghost',
        'destructive-text',
        'link',
        'ai',
        'grey-light',
        'primary-outline-rounded',
        'secondary-outline',
        'primary-bright',
        'button-link',
        'link-secondary',
        'link-secondary-hover',
        'dashed-border',
    ]),
    /** Render an anchor pointing at this URL instead of a button */
    href: PropTypes.string,
    /** Render nothing */
    hidden: PropTypes.bool,
    /** Render an anchor without href */
    isLink: PropTypes.bool,
    /** Wrap the label in a span (with textClassName) */
    wrapText: PropTypes.bool,
    /** Extra class for the label span */
    textClassName: PropTypes.string,
    /** Size from the design-system scale; without it the button keeps the legacy base size */
    size: PropTypes.oneOf(['xxs', 'xs', 'sm', 'md', 'lg']),
    /** Square icon-only button — pass the icon via iconLeft (or children) */
    onlyIcon: PropTypes.bool,
    /** Legacy `btn--new` styling flag */
    v2: PropTypes.bool,
    /** Remove padding */
    noPadding: PropTypes.bool,
    /** Icon element rendered before the label, e.g. `<Icon value='plus' noMargin />` */
    iconLeft: PropTypes.node,
    /** Icon element rendered after the label */
    iconRight: PropTypes.node,
    /** Selected/toggled state (red tint), e.g. an active filter */
    active: PropTypes.bool,
    /** Loading state — shows a spinner and blocks interaction */
    loading: PropTypes.bool,
    /** Stretch to the container's full width */
    fullWidth: PropTypes.bool,
    /** Native button type — `type` is taken by the visual variant. Defaults to "button" */
    htmlType: PropTypes.oneOf(['button', 'submit', 'reset']),
    /** type="ai" only: chevron dropdown indicator after the label */
    hasDropdown: PropTypes.bool,
    /** Native `title` attribute (tooltip) */
    altTitle: PropTypes.string,
};

ButtonGroup.propTypes = {
    /** Extra CSS class */
    className: PropTypes.string,
    /** The buttons to join */
    children: PropTypes.node,
};

export default Button;
